import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import { useStateCallbackRef } from '../hooks';
import { getReviewsList, getUserLikedReviews } from '../actions/reviews.actions';
import LoadingSpinner from './tailwind/LoadingSpinner';
import ProductReviewsItem from './ProductReviewsItem';
import { ProductReviewsCustomerImages } from './ProductReviewsCustomerImages';
import ProductReviewsModal from './ProductReviewsModal';
import ProductReviewsRating from './ProductReviewsRating';
import YotpoReviewsWidget from './YotpoReviewsWidget';

const ProductReviews = ({ variants, showYotpoReviewsWidget }) => {
  const dispatch = useAppDispatch();
  const { details, site } = useSelector((state) => state.pdpItem);
  const { loading, reviews, isLastPage, typeId: reviewsTypeId } = useSelector((state) => state.reviewsList);
  const typeId = details?.attributes?.typeId;

  useEffect(() => {
    if (typeId !== reviewsTypeId) getReviewsBatch();
  }, [typeId, reviewsTypeId, dispatch]);

  useEffect(() => {
    dispatch(getUserLikedReviews());
  }, [dispatch]);

  const [productReviewsModal, setProductReviewsModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const toggleModalHandler = useCallback(
    (image = undefined) => {
      if (productReviewsModal) {
        document.body.classList.remove('no-scroll');
        setProductReviewsModal(false);
      } else {
        if (image) {
          setSelectedReview({ review: reviews.find((i) => i.id === image.id), image: image.url });
        } else {
          setSelectedReview(null);
        }

        document.body.classList.add('no-scroll');
        setProductReviewsModal(true);
      }
    },
    [productReviewsModal, selectedReview, setProductReviewsModal, reviews]
  );

  const getReviewsBatch = useCallback(() => {
    if (typeId) {
      dispatch(getReviewsList(typeId));
    }
  }, [typeId, dispatch]);

  const [anchor, anchorCallbackRef] = useStateCallbackRef();
  useEffect(() => {
    if (!anchor || window.location.hash !== '#reviews') return;

    const scrollToAnchor = () => {
      window.scrollTo(0, anchor.offsetTop);
    };

    if (document.readyState === 'complete') {
      scrollToAnchor();
      return;
    } else {
      window.addEventListener('load', scrollToAnchor);

      return () => window.removeEventListener('load', scrollToAnchor);
    }
  }, [anchor]);

  return details ? (
    <section id='ProductReviews'>
      <a id='reviews' ref={anchorCallbackRef} />

      {showYotpoReviewsWidget && (
        <div className='container'>
          <div className='row'>
            <div className='col-12'>
              <h2 className='tw-font-body tw-text-2xl tw-font-bold tw-uppercase'>Ratings &amp; Reviews</h2>
            </div>
          </div>
          <div className='row'>
            <div className='col-12'>
              <YotpoReviewsWidget
                productId={`${Math.abs(details.itemId)}-${details.uom.id}`}
                price={`${details.rate}`}
                currency='USD'
                name={details.displayName}
                url={`${site.url}${details.url}`}
                image={details.webImages.default.active.url}
                description={details.metaDescription}
              />
            </div>
          </div>
        </div>
      )}
      {!showYotpoReviewsWidget && (
        <div className='container'>
          <div className='row'>
            <div className='col-12'>
              <h2>Ratings &amp; Reviews</h2>

              <div className='row'>
                <div className='col-lg-7 order-lg-2'>
                  {/* Hide customer images temporarily */}
                  {reviews?.length > 0 && false && (
                    <ProductReviewsCustomerImages
                      toggleModalHandler={toggleModalHandler}
                      reviews={reviews.filter((i) => i.images.length > 0)}
                    />
                  )}
                </div>
                <div className='col-lg-5 order-lg-1'>
                  <ProductReviewsRating variants={variants} />
                </div>
              </div>

              {reviews?.length > 0 && (
                <div className='row product-reviews-list'>
                  <div className='col-12'>
                    <h3>Customer Reviews</h3>

                    {reviews.map((review) => (
                      <ProductReviewsItem
                        key={review.id}
                        review={review}
                        imagesShown
                        toggleModalHandler={toggleModalHandler}
                      />
                    ))}

                    {loading ? (
                      <LoadingSpinner inline />
                    ) : (
                      !isLastPage && (
                        <button onClick={getReviewsBatch} className='twc-btn twc-btn--primary tw-mt-3'>
                          View More...
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {productReviewsModal && reviews?.filter((i) => i.images.length > 0).length > 0 && (
        <ProductReviewsModal
          typeAttribute={details.attributes.itemType}
          toggleHandler={toggleModalHandler}
          reviews={reviews.filter((i) => i.images.length > 0)}
          defaultReview={selectedReview}
        />
      )}
    </section>
  ) : null;
};

export default memo(ProductReviews);
